import React, { useRef, useState, useContext } from 'react'

import { useBreakpoint } from 'components/Breakpoint'

import SelectList from '../SelectList'
import MenuContext from './menuContext'

type ButtonOptions = {
  active: boolean
  onClick: () => void
}

interface SubMenuTriggerProps<T, U> {
  items: T[] | ReadonlyArray<T>
  setItem?: (item: T) => void

  value: U
  headerItem?: React.ReactNode
  disabled?: boolean

  renderTrigger: (props: TriggerProps) => React.ReactNode
  renderItem: (item: T, options: ButtonOptions) => React.ReactNode
}

interface TriggerProps {
  active: boolean
  onClick: () => void
  forceHover: boolean
  ref: React.RefObject<HTMLButtonElement>
}

function SubMenu<T, U>(props: SubMenuTriggerProps<T, U>) {
  const {
    items,
    setItem,

    value,
    headerItem,
    disabled = false,

    renderTrigger,
    renderItem,
  } = props

  const [expanded, setExpanded] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isDesktop = useBreakpoint('sm')

  const context = useContext(MenuContext)
  const showDropdown = expanded && !disabled

  const parentActive = context.activePath.includes(value)

  const children = (
    <>
      {items.map((item: T, index: number) => (
        <React.Fragment key={index}>
          {renderItem(item, {
            active: parentActive && context.activePath.includes(item),
            onClick: () => {
              context.setValue(item, [value, item])
              setExpanded(() => false)

              // Call the external "setItem" in case it exists
              setItem?.(item)
            },
          })}
        </React.Fragment>
      ))}
    </>
  )

  const triggerOnClick = () => {
    !isDesktop && setExpanded(value => !value)
  }

  return (
    <div
      onMouseEnter={() => {
        isDesktop && !expanded && setExpanded(() => true)
      }}
      onMouseLeave={() => {
        isDesktop && expanded && setExpanded(() => false)
      }}>
      {/* Render the trigger as a customizable item */}

      {renderTrigger({
        active: parentActive,
        onClick: triggerOnClick,
        forceHover: expanded,
        ref: buttonRef,
      })}

      <SelectList
        expanded={showDropdown}
        placement="right"
        headerItem={headerItem}
        setExpanded={getState => {
          setExpanded(getState(expanded, buttonRef.current!))
        }}
        usePortal={false}>
        <MenuContext.Provider
          value={{
            ...context,
            setValue: (item, path) => {
              context.setValue(item, [value, ...path])
              setExpanded(() => false)
            },
          }}>
          {children}
        </MenuContext.Provider>
      </SelectList>
    </div>
  )
}

export default SubMenu
